import {
  DialogChat,
  DialogReplyPreview,
  DialogUserSummary,
  DialogVoiceMessage,
  ObjectId,
  PinnedDialogMessage,
} from "../../../types/DialogChat";
import { RealTimeBooking } from "../../../types/TypeSession";
import { TypeUserData } from "../../../types/TypeUsers";
import { isDialogChatMutedForUser } from "./globalChatReadState";

export type GlobalChatMessage = {
  messageId: string;
  user?:
    | {
        _id: string;
        name: { text: string; language: string }[];
        surname: { text: string; language: string }[];
        avatar?: string;
        avatarS3?: string;
      }
    | "System";
  text: string;
  sender: string;
  files: any[];
  voice?: DialogVoiceMessage;
  date: Date;
  realTimeBooking?: RealTimeBooking;
  readBy?: string[];
  mentions?: ObjectId[] | DialogUserSummary[];
  replyTo?: ObjectId;
  replyToMessage?: DialogReplyPreview;
  deletedAt?: string;
  deletedBy?: ObjectId | DialogUserSummary;
  editedAt?: string;
  editedBy?: ObjectId | DialogUserSummary;
  reactions: {
    emoji: string;
    reactBy: Pick<TypeUserData, "name" | "surname" | "_id">;
  }[];
  deliveryState?: "pending" | "queued" | "sent" | "failed" | "retrying";
  isLocalOnly?: boolean;
  retryPayload?: {
    kind?: "text" | "voice";
    text: string;
    files: File[];
    voiceDraft?: {
      file: File;
      url: string;
      durationSeconds?: number;
    };
    mentions?: ObjectId[];
    replyTo?: ObjectId;
    replyToMessage?: DialogReplyPreview;
  };
};

export type MessageReadPayload = {
  _id: string;
  readBy: string[];
};

export type MessagesByChatEntry = {
  byId: Record<string, GlobalChatMessage>;
  ids: string[];
  page: number;
  hasMore: boolean;
  newestPage: number;
  hasNewer: boolean;
  initialPageRequested: boolean;
  isLoading: boolean;
  pendingRequests: Record<number, number>;
};

export type GlobalChatCacheState = {
  conversationsById: Record<string, DialogChat>;
  conversationIds: string[];
  selectedChatId: string | null;
  messagesByChatId: Record<string, MessagesByChatEntry>;
  lastMessageByChatId: Record<
    string,
    {
      message: string;
      date: Date | undefined;
      messageId?: string;
    }
  >;
  conversationsHasMore: boolean;
  conversationsLoading: boolean;
  conversationsLoaded: boolean;
  conversationsSearch: string;
};

export type GlobalChatCacheAction =
  | {
      type: "reset";
    }
  | {
      type: "setConversationsSearch";
      search: string;
    }
  | {
      type: "loadConversationsStart";
    }
  | {
      type: "loadConversationsSuccess";
      conversations: DialogChat[];
      page: number;
      hasMore: boolean;
    }
  | {
      type: "loadConversationsError";
    }
  | {
      type: "selectConversation";
      chatId: string | null;
    }
  | {
      type: "upsertConversation";
      conversation: DialogChat;
      position?: "start" | "end";
    }
  | {
      type: "removeConversation";
      chatId: string;
    }
  | {
      type: "updateConversation";
      chatId: string;
      changes: Partial<DialogChat>;
    }
  | {
      type: "applyPinnedMessage";
      chatId: string;
      pinnedMessage: PinnedDialogMessage;
    }
  | {
      type: "removePinnedMessage";
      chatId: string;
      messageId: string;
    }
  | {
      type: "loadMessagesStart";
      chatId: string;
      page: number;
      requestId: number;
    }
  | {
      type: "loadMessagesSuccess";
      chatId: string;
      page: number;
      requestId: number;
      messages: GlobalChatMessage[];
      hasMore: boolean;
    }
  | {
      type: "loadMessagesError";
      chatId: string;
      page: number;
      requestId: number;
    }
  | {
      type: "replaceMessagesAroundTarget";
      chatId: string;
      page: number;
      messages: GlobalChatMessage[];
      hasMore: boolean;
      hasNewer: boolean;
    }
  | {
      type: "appendMessage";
      chatId: string;
      message: GlobalChatMessage;
      currentUserId?: string;
    }
  | {
      type: "replaceLocalMessage";
      chatId: string;
      localMessageId: string;
      message: GlobalChatMessage;
    }
  | {
      type: "replaceMessage";
      messageId: string;
      message: GlobalChatMessage;
    }
  | {
      type: "deleteMessageEverywhere";
      messageId: string;
    }
  | {
      type: "updateLocalMessageDeliveryState";
      chatId: string;
      messageId: string;
      deliveryState: NonNullable<GlobalChatMessage["deliveryState"]>;
    }
  | {
      type: "discardLocalMessage";
      chatId: string;
      messageId: string;
    }
  | {
      type: "updateMessage";
      messageId: string;
      changes: Partial<GlobalChatMessage>;
    }
  | {
      type: "applyMessageReactions";
      messageId: string;
      emoji: string;
      reactBy: Pick<TypeUserData, "name" | "surname" | "_id">;
      reactionAdded?: boolean;
    }
  | {
      type: "applyMessagesRead";
      updatedMessages: MessageReadPayload[];
    }
  | {
      type: "markConversationRead";
      chatId: string;
    };

export const createInitialGlobalChatCacheState = (): GlobalChatCacheState => ({
  conversationsById: {},
  conversationIds: [],
  selectedChatId: null,
  messagesByChatId: {},
  lastMessageByChatId: {},
  conversationsHasMore: true,
  conversationsLoading: false,
  conversationsLoaded: false,
  conversationsSearch: "",
});

const getEmptyMessagesEntry = (): MessagesByChatEntry => ({
  byId: {},
  ids: [],
  page: 1,
  hasMore: true,
  newestPage: 1,
  hasNewer: false,
  initialPageRequested: false,
  isLoading: false,
  pendingRequests: {},
});

const upsertOrderedIds = (
  ids: string[],
  id: string,
  position: "start" | "end" = "end",
) => {
  const filteredIds = ids.filter((existingId) => existingId !== id);
  return position === "start" ? [id, ...filteredIds] : [...filteredIds, id];
};

const mergeMessages = (
  entry: MessagesByChatEntry,
  messages: GlobalChatMessage[],
  placement: "prepend" | "append",
): MessagesByChatEntry => {
  const byId = { ...entry.byId };
  const incomingIds: string[] = [];

  messages.forEach((message) => {
    byId[message.messageId] = message;
    if (!entry.ids.includes(message.messageId)) {
      incomingIds.push(message.messageId);
    }
  });

  const ids =
    placement === "prepend"
      ? [...incomingIds, ...entry.ids]
      : [...entry.ids, ...incomingIds];

  return {
    ...entry,
    byId,
    ids,
  };
};

const removeMessage = (
  entry: MessagesByChatEntry,
  messageId: string,
): MessagesByChatEntry => {
  const { [messageId]: _removedMessage, ...byId } = entry.byId;

  return {
    ...entry,
    byId,
    ids: entry.ids.filter((id) => id !== messageId),
  };
};

const replaceLocalMessage = (
  entry: MessagesByChatEntry,
  localMessageId: string,
  message: GlobalChatMessage,
): MessagesByChatEntry => {
  const byId = { ...entry.byId };
  const serverMessageAlreadyExists = Boolean(byId[message.messageId]);
  delete byId[localMessageId];
  byId[message.messageId] = message;

  const ids = entry.ids.reduce<string[]>((acc, id) => {
    if (id === localMessageId) {
      return serverMessageAlreadyExists ? acc : [...acc, message.messageId];
    }

    if (id === message.messageId && acc.includes(message.messageId)) {
      return acc;
    }

    return [...acc, id];
  }, []);

  return {
    ...entry,
    byId,
    ids: ids.includes(message.messageId) ? ids : [...ids, message.messageId],
  };
};

const updateReplyPreviews = (
  entry: MessagesByChatEntry,
  changedMessage: GlobalChatMessage,
): MessagesByChatEntry => {
  let didChange = false;
  const byId = { ...entry.byId };

  entry.ids.forEach((messageId) => {
    const message = byId[messageId];
    if (message.replyToMessage?._id !== changedMessage.messageId) {
      return;
    }

    didChange = true;
    byId[messageId] = {
      ...message,
      replyToMessage: {
        ...message.replyToMessage,
        message: changedMessage.voice
          ? ""
          : changedMessage.text ||
            (changedMessage.files.length > 0 ? "" : undefined),
        files: changedMessage.files,
        labels: undefined,
        date: new Date(changedMessage.date).toISOString(),
      },
    };
  });

  return didChange ? { ...entry, byId } : entry;
};

const removeDeletedReplyPreviews = (
  entry: MessagesByChatEntry,
  deletedMessageId: string,
): MessagesByChatEntry => {
  let didChange = false;
  const byId = { ...entry.byId };

  entry.ids.forEach((messageId) => {
    const message = byId[messageId];
    if (message.replyToMessage?._id !== deletedMessageId) {
      return;
    }

    didChange = true;
    byId[messageId] = {
      ...message,
      replyTo: undefined,
      replyToMessage: undefined,
    };
  });

  return didChange ? { ...entry, byId } : entry;
};

const applyReadBy = (
  entry: MessagesByChatEntry,
  readByByMessageId: Map<string, string[]>,
): MessagesByChatEntry => {
  let didChange = false;
  const byId = { ...entry.byId };

  entry.ids.forEach((messageId) => {
    const readBy = readByByMessageId.get(messageId);
    if (readBy) {
      didChange = true;
      byId[messageId] = {
        ...byId[messageId],
        readBy,
      };
    }
  });

  return didChange ? { ...entry, byId } : entry;
};

const getLastMessagePreview = (entry: MessagesByChatEntry) => {
  const lastMessageId = entry.ids[entry.ids.length - 1];
  const lastMessage = lastMessageId ? entry.byId[lastMessageId] : undefined;

  return lastMessage
    ? {
        message: lastMessage.text || "File",
        date: lastMessage.date,
        messageId: lastMessage.messageId,
      }
    : undefined;
};

const getPinnedMessageId = (pin: PinnedDialogMessage) =>
  typeof pin.message === "string" ? pin.message : pin.message._id;

const upsertPinnedMessage = (
  pinnedMessages: PinnedDialogMessage[] | undefined,
  pinnedMessage: PinnedDialogMessage,
) => {
  const messageId = getPinnedMessageId(pinnedMessage);
  const nextPins = (pinnedMessages || []).filter(
    (pin) => getPinnedMessageId(pin) !== messageId,
  );

  return [pinnedMessage, ...nextPins].sort(
    (a, b) => new Date(b.pinnedAt).getTime() - new Date(a.pinnedAt).getTime(),
  );
};

const removePinnedMessage = (
  pinnedMessages: PinnedDialogMessage[] | undefined,
  messageId: string,
) =>
  (pinnedMessages || []).filter((pin) => getPinnedMessageId(pin) !== messageId);

export const selectConversations = (state: GlobalChatCacheState) =>
  state.conversationIds
    .map((chatId) => state.conversationsById[chatId])
    .filter(Boolean);

export const selectMessagesForChat = (
  state: GlobalChatCacheState,
  chatId?: string | null,
) => {
  if (!chatId) {
    return [];
  }

  const entry = state.messagesByChatId[chatId];
  if (!entry) {
    return [];
  }

  return entry.ids.map((messageId) => entry.byId[messageId]).filter(Boolean);
};

export const globalChatCacheReducer = (
  state: GlobalChatCacheState,
  action: GlobalChatCacheAction,
): GlobalChatCacheState => {
  switch (action.type) {
    case "reset":
      return createInitialGlobalChatCacheState();

    case "setConversationsSearch":
      return {
        ...state,
        conversationsSearch: action.search,
        conversationsHasMore: true,
      };

    case "loadConversationsStart":
      return {
        ...state,
        conversationsLoading: true,
      };

    case "loadConversationsSuccess": {
      const conversationsById =
        action.page === 0 ? {} : { ...state.conversationsById };
      const conversationIds =
        action.page === 0 ? [] : [...state.conversationIds];

      action.conversations.forEach((conversation) => {
        conversationsById[conversation._id] = conversation;
        if (!conversationIds.includes(conversation._id)) {
          conversationIds.push(conversation._id);
        }
      });

      return {
        ...state,
        conversationsById,
        conversationIds,
        conversationsHasMore: action.hasMore,
        conversationsLoading: false,
        conversationsLoaded: true,
      };
    }

    case "loadConversationsError":
      return {
        ...state,
        conversationsLoading: false,
      };

    case "selectConversation":
      return {
        ...state,
        selectedChatId: action.chatId,
        messagesByChatId: action.chatId
          ? {
              ...state.messagesByChatId,
              [action.chatId]: {
                ...getEmptyMessagesEntry(),
                ...(state.messagesByChatId[action.chatId] || {}),
                page: 1,
                hasMore: state.messagesByChatId[action.chatId]?.hasMore ?? true,
              },
            }
          : state.messagesByChatId,
      };

    case "upsertConversation":
      return {
        ...state,
        conversationsById: {
          ...state.conversationsById,
          [action.conversation._id]: action.conversation,
        },
        conversationIds: upsertOrderedIds(
          state.conversationIds,
          action.conversation._id,
          action.position,
        ),
      };

    case "removeConversation": {
      const { [action.chatId]: _removedConversation, ...conversationsById } =
        state.conversationsById;
      const { [action.chatId]: _removedMessages, ...messagesByChatId } =
        state.messagesByChatId;
      const { [action.chatId]: _removedLastMessage, ...lastMessageByChatId } =
        state.lastMessageByChatId;

      return {
        ...state,
        conversationsById,
        messagesByChatId,
        lastMessageByChatId,
        conversationIds: state.conversationIds.filter(
          (chatId) => chatId !== action.chatId,
        ),
        selectedChatId:
          state.selectedChatId === action.chatId ? null : state.selectedChatId,
      };
    }

    case "updateConversation":
      if (!state.conversationsById[action.chatId]) {
        return state;
      }

      return {
        ...state,
        conversationsById: {
          ...state.conversationsById,
          [action.chatId]: {
            ...state.conversationsById[action.chatId],
            ...action.changes,
          },
        },
      };

    case "applyPinnedMessage": {
      const conversation = state.conversationsById[action.chatId];
      if (!conversation) {
        return state;
      }

      return {
        ...state,
        conversationsById: {
          ...state.conversationsById,
          [action.chatId]: {
            ...conversation,
            pinnedMessages: upsertPinnedMessage(
              conversation.pinnedMessages,
              action.pinnedMessage,
            ),
          },
        },
      };
    }

    case "removePinnedMessage": {
      const conversation = state.conversationsById[action.chatId];
      if (!conversation) {
        return state;
      }

      return {
        ...state,
        conversationsById: {
          ...state.conversationsById,
          [action.chatId]: {
            ...conversation,
            pinnedMessages: removePinnedMessage(
              conversation.pinnedMessages,
              action.messageId,
            ),
          },
        },
      };
    }

    case "loadMessagesStart":
      {
        const entry =
          state.messagesByChatId[action.chatId] || getEmptyMessagesEntry();

        return {
          ...state,
          messagesByChatId: {
            ...state.messagesByChatId,
            [action.chatId]: {
              ...entry,
              initialPageRequested:
                entry.initialPageRequested || action.page === 1,
              isLoading: true,
              pendingRequests: {
                ...entry.pendingRequests,
                [action.page]: action.requestId,
              },
            },
          },
        };
      }

    case "loadMessagesSuccess": {
      const entry = state.messagesByChatId[action.chatId];
      if (!entry || entry.pendingRequests[action.page] !== action.requestId) {
        return state;
      }
      const { [action.page]: _completedRequest, ...pendingRequests } =
        entry.pendingRequests;
      const shouldAppendNewer = action.page < entry.newestPage;
      const nextEntry = mergeMessages(
        entry,
        action.messages,
        shouldAppendNewer ? "append" : "prepend",
      );

      return {
        ...state,
        messagesByChatId: {
          ...state.messagesByChatId,
          [action.chatId]: {
            ...nextEntry,
            page: Math.max(entry.page, action.page),
            newestPage: Math.min(entry.newestPage, action.page),
            hasMore: shouldAppendNewer ? entry.hasMore : action.hasMore,
            hasNewer: shouldAppendNewer ? action.page > 1 : entry.hasNewer,
            isLoading: Object.keys(pendingRequests).length > 0,
            pendingRequests,
          },
        },
      };
    }

    case "loadMessagesError": {
      const entry = state.messagesByChatId[action.chatId];
      if (!entry || entry.pendingRequests[action.page] !== action.requestId) {
        return state;
      }
      const { [action.page]: _failedRequest, ...pendingRequests } =
        entry.pendingRequests;

      return {
        ...state,
        messagesByChatId: {
          ...state.messagesByChatId,
          [action.chatId]: {
            ...entry,
            isLoading: Object.keys(pendingRequests).length > 0,
            pendingRequests,
          },
        },
      };
    }

    case "replaceMessagesAroundTarget": {
      const entry = getEmptyMessagesEntry();
      const byId = action.messages.reduce<Record<string, GlobalChatMessage>>(
        (acc, message) => ({
          ...acc,
          [message.messageId]: message,
        }),
        {},
      );
      const ids = action.messages.map((message) => message.messageId);

      return {
        ...state,
        messagesByChatId: {
          ...state.messagesByChatId,
          [action.chatId]: {
            ...entry,
            byId,
            ids,
            page: action.page,
            hasMore: action.hasMore,
            newestPage: action.page,
            hasNewer: action.hasNewer,
            isLoading: false,
          },
        },
      };
    }

    case "appendMessage": {
      const entry =
        state.messagesByChatId[action.chatId] || getEmptyMessagesEntry();
      const lastMessage = {
        message: action.message.text || "File",
        date: action.message.date,
        messageId: action.message.messageId,
      };
      const conversation = state.conversationsById[action.chatId];
      const shouldIncrementUnread =
        conversation &&
        state.selectedChatId !== action.chatId &&
        action.message.sender === "other" &&
        !isDialogChatMutedForUser(conversation, action.currentUserId);

      return {
        ...state,
        conversationsById: shouldIncrementUnread
          ? {
              ...state.conversationsById,
              [action.chatId]: {
                ...conversation,
                unreadMessages: conversation.unreadMessages + 1,
              },
            }
          : state.conversationsById,
        messagesByChatId: {
          ...state.messagesByChatId,
          [action.chatId]: mergeMessages(entry, [action.message], "append"),
        },
        lastMessageByChatId: {
          ...state.lastMessageByChatId,
          [action.chatId]: lastMessage,
        },
      };
    }

    case "replaceLocalMessage": {
      const entry = state.messagesByChatId[action.chatId];
      if (!entry || !entry.byId[action.localMessageId]) {
        return state;
      }

      return {
        ...state,
        messagesByChatId: {
          ...state.messagesByChatId,
          [action.chatId]: replaceLocalMessage(
            entry,
            action.localMessageId,
            action.message,
          ),
        },
        lastMessageByChatId: {
          ...state.lastMessageByChatId,
          [action.chatId]: {
            message: action.message.text || "File",
            date: action.message.date,
            messageId: action.message.messageId,
          },
        },
      };
    }

    case "replaceMessage": {
      const messagesByChatId = Object.entries(state.messagesByChatId).reduce(
        (acc, [chatId, entry]) => {
          if (!entry.byId[action.messageId]) {
            return {
              ...acc,
              [chatId]: updateReplyPreviews(entry, action.message),
            };
          }

          const byId = { ...entry.byId };
          delete byId[action.messageId];
          byId[action.message.messageId] = action.message;
          const ids = entry.ids.map((id) =>
            id === action.messageId ? action.message.messageId : id,
          );

          return {
            ...acc,
            [chatId]: updateReplyPreviews(
              {
                ...entry,
                byId,
                ids: Array.from(new Set(ids)),
              },
              action.message,
            ),
          };
        },
        {} as Record<string, MessagesByChatEntry>,
      );
      const lastMessageByChatId = { ...state.lastMessageByChatId };

      Object.entries(messagesByChatId).forEach(([chatId, entry]) => {
        if (lastMessageByChatId[chatId]?.messageId === action.messageId) {
          lastMessageByChatId[chatId] = {
            message: action.message.text || "File",
            date: action.message.date,
            messageId: action.message.messageId,
          };
          return;
        }

        const preview = getLastMessagePreview(entry);
        if (preview) {
          lastMessageByChatId[chatId] = preview;
        }
      });

      return {
        ...state,
        messagesByChatId,
        lastMessageByChatId,
      };
    }

    case "deleteMessageEverywhere": {
      const messagesByChatId = Object.entries(state.messagesByChatId).reduce(
        (acc, [chatId, entry]) => ({
          ...acc,
          [chatId]: removeDeletedReplyPreviews(
            removeMessage(entry, action.messageId),
            action.messageId,
          ),
        }),
        {} as Record<string, MessagesByChatEntry>,
      );
      const lastMessageByChatId = { ...state.lastMessageByChatId };

      Object.entries(messagesByChatId).forEach(([chatId, entry]) => {
        if (lastMessageByChatId[chatId]?.messageId !== action.messageId) {
          return;
        }

        const preview = getLastMessagePreview(entry);
        if (preview) {
          lastMessageByChatId[chatId] = preview;
        } else {
          delete lastMessageByChatId[chatId];
        }
      });

      const conversationsById = Object.entries(state.conversationsById).reduce(
        (acc, [chatId, conversation]) => ({
          ...acc,
          [chatId]: {
            ...conversation,
            pinnedMessages: conversation.pinnedMessages?.filter((pin) =>
              typeof pin.message === "string"
                ? pin.message !== action.messageId
                : pin.message._id !== action.messageId,
            ),
          },
        }),
        {} as Record<string, DialogChat>,
      );

      return {
        ...state,
        conversationsById,
        messagesByChatId,
        lastMessageByChatId,
      };
    }

    case "updateLocalMessageDeliveryState": {
      const entry = state.messagesByChatId[action.chatId];
      const message = entry?.byId[action.messageId];
      if (!entry || !message?.isLocalOnly) {
        return state;
      }

      return {
        ...state,
        messagesByChatId: {
          ...state.messagesByChatId,
          [action.chatId]: {
            ...entry,
            byId: {
              ...entry.byId,
              [action.messageId]: {
                ...message,
                deliveryState: action.deliveryState,
              },
            },
          },
        },
      };
    }

    case "discardLocalMessage": {
      const entry = state.messagesByChatId[action.chatId];
      const message = entry?.byId[action.messageId];
      if (!entry || !message?.isLocalOnly) {
        return state;
      }

      return {
        ...state,
        messagesByChatId: {
          ...state.messagesByChatId,
          [action.chatId]: removeMessage(entry, action.messageId),
        },
      };
    }

    case "updateMessage":
      return {
        ...state,
        messagesByChatId: Object.entries(state.messagesByChatId).reduce(
          (acc, [chatId, entry]) => {
            const message = entry.byId[action.messageId];
            if (!message) {
              return {
                ...acc,
                [chatId]: entry,
              };
            }

            return {
              ...acc,
              [chatId]: {
                ...entry,
                byId: {
                  ...entry.byId,
                  [action.messageId]: {
                    ...message,
                    ...action.changes,
                  },
                },
              },
            };
          },
          {} as Record<string, MessagesByChatEntry>,
        ),
      };

    case "applyMessageReactions":
      return {
        ...state,
        messagesByChatId: Object.entries(state.messagesByChatId).reduce(
          (acc, [chatId, entry]) => {
            if (!entry.byId[action.messageId]) {
              return {
                ...acc,
                [chatId]: entry,
              };
            }

            const message = entry.byId[action.messageId];
            const existingReaction = message.reactions.find(
              (reaction) =>
                reaction.emoji === action.emoji &&
                reaction.reactBy?._id === action.reactBy?._id,
            );
            const shouldAdd =
              action.reactionAdded ?? existingReaction === undefined;
            const reactions = shouldAdd
              ? existingReaction
                ? message.reactions
                : [
                    ...message.reactions,
                    {
                      emoji: action.emoji,
                      reactBy: action.reactBy,
                    },
                  ]
              : message.reactions.filter(
                  (reaction) => reaction !== existingReaction,
                );

            return {
              ...acc,
              [chatId]: {
                ...entry,
                byId: {
                  ...entry.byId,
                  [action.messageId]: {
                    ...message,
                    reactions,
                  },
                },
              },
            };
          },
          {} as Record<string, MessagesByChatEntry>,
        ),
      };

    case "applyMessagesRead": {
      const readByByMessageId = new Map(
        action.updatedMessages.map((message) => [message._id, message.readBy]),
      );

      return {
        ...state,
        messagesByChatId: Object.entries(state.messagesByChatId).reduce(
          (acc, [chatId, entry]) => ({
            ...acc,
            [chatId]: applyReadBy(entry, readByByMessageId),
          }),
          {} as Record<string, MessagesByChatEntry>,
        ),
      };
    }

    case "markConversationRead":
      if (!state.conversationsById[action.chatId]) {
        return state;
      }

      return {
        ...state,
        conversationsById: {
          ...state.conversationsById,
          [action.chatId]: {
            ...state.conversationsById[action.chatId],
            unreadMessages: 0,
          },
        },
      };

    default:
      return state;
  }
};
